import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import {
    FaWhatsapp,
    FaInstagram,
    FaFacebook,
    FaYoutube,
    FaTelegramPlane,
    FaPhoneAlt,
    FaShareAlt,
    FaLink,
    FaPlus,
    FaEdit,
    FaTrash,
    FaCheckCircle,
    FaToggleOn
} from "react-icons/fa";

import API_BASE_URL from "../config/api";


function SocialLinks() {

    const navigate = useNavigate();

    const [socialLinks, setSocialLinks] = useState(null);

    const [loading, setLoading] = useState(true);


    /*
    |--------------------------------------------------------------------------
    | Fetch Social Links
    |--------------------------------------------------------------------------
    */

    const fetchSocialLinks = async () => {

        try {

            setLoading(true);

            const response = await fetch(
                `${API_BASE_URL}/admin/social-links`,
                {
                    method: "GET",

                    headers: {
                        "Accept": "application/json"
                    },

                    credentials: "include"
                }
            );


            const data = await response.json();


            console.log(
                "Social Links API Response:",
                data
            );


            if (response.ok && data.success) {

                const record =
                    data.social_links ||
                    data.social_link ||
                    null;


                setSocialLinks(record);

            } else {

                setSocialLinks(null);

                Swal.fire({
                    icon: "error",
                    title: "Unable to Load",
                    text:
                        data.message ||
                        "Unable to fetch social links."
                });
            }

        } catch (error) {

            console.error(
                "Social Links Error:",
                error
            );


            Swal.fire({
                icon: "error",
                title: "Server Error",
                text:
                    "Unable to connect with server."
            });

        } finally {

            setLoading(false);
        }
    };


    /*
    |--------------------------------------------------------------------------
    | Load Data
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        fetchSocialLinks();

    }, []);


    /*
    |--------------------------------------------------------------------------
    | Delete Social Links
    |--------------------------------------------------------------------------
    */

    const handleDelete = async () => {

        if (!socialLinks?.id) {

            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Social link ID not found."
            });

            return;
        }


        const result = await Swal.fire({

            icon: "warning",

            title: "Delete Social Links?",

            text:
                "All social media links will be deleted.",

            showCancelButton: true,

            confirmButtonColor: "#dc3545",

            cancelButtonColor: "#6c757d",

            confirmButtonText: "Yes, Delete",

            cancelButtonText: "Cancel"
        });


        if (!result.isConfirmed) {
            return;
        }


        try {

            const response = await fetch(
                `${API_BASE_URL}/admin/social-links/${socialLinks.id}`,
                {
                    method: "DELETE",

                    headers: {
                        "Accept": "application/json"
                    },

                    credentials: "include"
                }
            );


            const data = await response.json();


            if (response.ok && data.success) {

                setSocialLinks(null);


                await Swal.fire({

                    icon: "success",

                    title: "Deleted!",

                    text:
                        data.message ||
                        "Social links deleted successfully.",

                    timer: 1500,

                    showConfirmButton: false
                });

            } else {

                Swal.fire({

                    icon: "error",

                    title: "Unable to Delete",

                    text:
                        data.message ||
                        "Something went wrong."
                });
            }

        } catch (error) {

            console.error(
                "Delete Social Links Error:",
                error
            );


            Swal.fire({

                icon: "error",

                title: "Server Error",

                text:
                    "Unable to connect with server."
            });
        }
    };


    /*
    |--------------------------------------------------------------------------
    | Loading
    |--------------------------------------------------------------------------
    */

    if (loading) {

        return (

            <div className="container-fluid py-4">

                <div className="card border-0 shadow-sm rounded-4">

                    <div className="card-body text-center py-5">

                        <div
                            className="spinner-border text-primary"
                            role="status"
                        >
                            <span className="visually-hidden">
                                Loading...
                            </span>
                        </div>


                        <p className="text-muted mt-3 mb-0">
                            Loading social links...
                        </p>

                    </div>

                </div>

            </div>

        );
    }


    /*
    |--------------------------------------------------------------------------
    | Main UI
    |--------------------------------------------------------------------------
    */

    return (

         <div
            className="container-fluid py-4"
            style={{
                maxHeight: "500px",
                overflowX: "auto",
                overflowY: "auto"
            }}
        >


            {/* ==============================================================
                PAGE HEADER
            ============================================================== */}

            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">

                <div>

                    <div className="d-flex align-items-center mb-1">

                        <div
                            className="bg-primary text-white rounded-3 d-flex align-items-center justify-content-center me-3 shadow-sm"
                            style={{
                                width: "48px",
                                height: "48px"
                            }}
                        >
                            <FaShareAlt size={21} />
                        </div>


                        <div>

                            <h3 className="fw-bold mb-0">
                                Social Links
                            </h3>

                            <p className="text-muted mb-0">
                                Manage website social media and contact details
                            </p>

                        </div>

                    </div>

                </div>


                {!socialLinks && (

                    <button
                        type="button"
                        className="btn btn-primary px-4 shadow-sm"
                        onClick={() =>
                            navigate("/add-social-links")
                        }
                    >

                        <FaPlus
                            size={13}
                            className="me-2"
                        />

                        Add Social Links

                    </button>

                )}

            </div>


            {/* ==============================================================
                MAIN CARD
            ============================================================== */}

            <div className="card border-0 shadow-sm rounded-4 overflow-hidden">


                {/* ==========================================================
                    CARD HEADER
                ========================================================== */}

                <div className="card-header bg-white border-bottom py-3 px-4">

                    <div className="d-flex align-items-center">

                        <div
                            className="bg-primary bg-opacity-10 text-primary rounded-3 d-flex align-items-center justify-content-center me-3"
                            style={{
                                width: "42px",
                                height: "42px"
                            }}
                        >

                            <FaShareAlt size={18} />

                        </div>


                        <div>

                            <h5 className="fw-bold mb-0">
                                Social Media Settings
                            </h5>

                            <small className="text-muted">
                                Website contact and social media information
                            </small>

                        </div>

                    </div>

                </div>


                {/* ==========================================================
                    CARD BODY
                ========================================================== */}

                <div className="card-body p-4">


                    {/* ======================================================
                        NO DATA
                    ====================================================== */}

                    {!socialLinks ? (

                        <div className="text-center py-5">

                            <div
                                className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
                                style={{
                                    width: "85px",
                                    height: "85px"
                                }}
                            >

                                <FaShareAlt
                                    className="text-secondary"
                                    size={34}
                                />

                            </div>


                            <h5 className="fw-bold mb-2">
                                No Social Links Found
                            </h5>


                            <p className="text-muted mb-4">

                                Add WhatsApp, Instagram, Facebook,
                                YouTube, Telegram and mobile number.

                            </p>


                            <button
                                type="button"
                                className="btn btn-primary px-4"
                                onClick={() =>
                                    navigate("/add-social-links")
                                }
                            >

                                <FaPlus
                                    size={13}
                                    className="me-2"
                                />

                                Add Social Links

                            </button>

                        </div>

                    ) : (

                        <>


                            {/* ==================================================
                                SOCIAL LINKS GRID
                            ================================================== */}

                            <div className="row g-4">


                                {/* ==================================================
                                    WHATSAPP
                                ================================================== */}

                                <div className="col-12 col-md-6">

                                    <div className="border rounded-4 p-3 h-100 bg-light">

                                        <div className="d-flex align-items-center mb-3">

                                            <div
                                                className="bg-success bg-opacity-10 text-success rounded-3 d-flex align-items-center justify-content-center me-3"
                                                style={{
                                                    width: "46px",
                                                    height: "46px"
                                                }}
                                            >

                                                <FaWhatsapp size={23} />

                                            </div>


                                            <div>

                                                <h6 className="fw-bold mb-0">
                                                    WhatsApp
                                                </h6>

                                                <small className="text-muted">
                                                    Contact Number
                                                </small>

                                            </div>

                                        </div>


                                        {socialLinks.whatsapp ? (

                                            <>

                                                <div className="fw-semibold mb-2">
                                                    {socialLinks.whatsapp}
                                                </div>


                                                <a
                                                    href={`https://wa.me/${socialLinks.whatsapp}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-decoration-none small text-break"
                                                >

                                                    <FaLink
                                                        size={12}
                                                        className="me-1"
                                                    />

                                                    https://wa.me/{socialLinks.whatsapp}

                                                </a>

                                            </>

                                        ) : (

                                            <span className="text-muted">
                                                Not Added
                                            </span>

                                        )}

                                    </div>

                                </div>


                                {/* ==================================================
                                    INSTAGRAM
                                ================================================== */}

                                <div className="col-12 col-md-6">

                                    <div className="border rounded-4 p-3 h-100 bg-light">

                                        <div className="d-flex align-items-center mb-3">

                                            <div
                                                className="bg-danger bg-opacity-10 text-danger rounded-3 d-flex align-items-center justify-content-center me-3"
                                                style={{
                                                    width: "46px",
                                                    height: "46px"
                                                }}
                                            >

                                                <FaInstagram size={23} />

                                            </div>


                                            <div>

                                                <h6 className="fw-bold mb-0">
                                                    Instagram
                                                </h6>

                                                <small className="text-muted">
                                                    Social Media
                                                </small>

                                            </div>

                                        </div>


                                        {socialLinks.instagram ? (

                                            <a
                                                href={socialLinks.instagram}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-decoration-none text-break small"
                                            >

                                                <FaLink
                                                    size={12}
                                                    className="me-1"
                                                />

                                                {socialLinks.instagram}

                                            </a>

                                        ) : (

                                            <span className="text-muted">
                                                Not Added
                                            </span>

                                        )}

                                    </div>

                                </div>


                                {/* ==================================================
                                    FACEBOOK
                                ================================================== */}

                                <div className="col-12 col-md-6">

                                    <div className="border rounded-4 p-3 h-100 bg-light">

                                        <div className="d-flex align-items-center mb-3">

                                            <div
                                                className="bg-primary bg-opacity-10 text-primary rounded-3 d-flex align-items-center justify-content-center me-3"
                                                style={{
                                                    width: "46px",
                                                    height: "46px"
                                                }}
                                            >

                                                <FaFacebook size={23} />

                                            </div>


                                            <div>

                                                <h6 className="fw-bold mb-0">
                                                    Facebook
                                                </h6>

                                                <small className="text-muted">
                                                    Social Media
                                                </small>

                                            </div>

                                        </div>


                                        {socialLinks.facebook ? (

                                            <a
                                                href={socialLinks.facebook}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-decoration-none text-break small"
                                            >

                                                <FaLink
                                                    size={12}
                                                    className="me-1"
                                                />

                                                {socialLinks.facebook}

                                            </a>

                                        ) : (

                                            <span className="text-muted">
                                                Not Added
                                            </span>

                                        )}

                                    </div>

                                </div>


                                {/* ==================================================
                                    YOUTUBE
                                ================================================== */}

                                <div className="col-12 col-md-6">

                                    <div className="border rounded-4 p-3 h-100 bg-light">

                                        <div className="d-flex align-items-center mb-3">

                                            <div
                                                className="bg-danger bg-opacity-10 text-danger rounded-3 d-flex align-items-center justify-content-center me-3"
                                                style={{
                                                    width: "46px",
                                                    height: "46px"
                                                }}
                                            >

                                                <FaYoutube size={23} />

                                            </div>


                                            <div>

                                                <h6 className="fw-bold mb-0">
                                                    YouTube
                                                </h6>

                                                <small className="text-muted">
                                                    Video Channel
                                                </small>

                                            </div>

                                        </div>


                                        {socialLinks.youtube ? (

                                            <a
                                                href={socialLinks.youtube}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-decoration-none text-break small"
                                            >

                                                <FaLink
                                                    size={12}
                                                    className="me-1"
                                                />

                                                {socialLinks.youtube}

                                            </a>

                                        ) : (

                                            <span className="text-muted">
                                                Not Added
                                            </span>

                                        )}

                                    </div>

                                </div>


                                {/* ==================================================
                                    TELEGRAM
                                ================================================== */}

                                <div className="col-12 col-md-6">

                                    <div className="border rounded-4 p-3 h-100 bg-light">

                                        <div className="d-flex align-items-center mb-3">

                                            <div
                                                className="bg-info bg-opacity-10 text-info rounded-3 d-flex align-items-center justify-content-center me-3"
                                                style={{
                                                    width: "46px",
                                                    height: "46px"
                                                }}
                                            >

                                                <FaTelegramPlane size={23} />

                                            </div>


                                            <div>

                                                <h6 className="fw-bold mb-0">
                                                    Telegram
                                                </h6>

                                                <small className="text-muted">
                                                    Telegram Channel
                                                </small>

                                            </div>

                                        </div>


                                        {socialLinks.telegram ? (

                                            <a
                                                href={socialLinks.telegram}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-decoration-none text-break small"
                                            >

                                                <FaLink
                                                    size={12}
                                                    className="me-1"
                                                />

                                                {socialLinks.telegram}

                                            </a>

                                        ) : (

                                            <span className="text-muted">
                                                Not Added
                                            </span>

                                        )}

                                    </div>

                                </div>


                                {/* ==================================================
                                    MOBILE
                                ================================================== */}

                                <div className="col-12 col-md-6">

                                    <div className="border rounded-4 p-3 h-100 bg-light">

                                        <div className="d-flex align-items-center mb-3">

                                            <div
                                                className="bg-secondary bg-opacity-10 text-secondary rounded-3 d-flex align-items-center justify-content-center me-3"
                                                style={{
                                                    width: "46px",
                                                    height: "46px"
                                                }}
                                            >

                                                <FaPhoneAlt size={20} />

                                            </div>


                                            <div>

                                                <h6 className="fw-bold mb-0">
                                                    Mobile
                                                </h6>

                                                <small className="text-muted">
                                                    Contact Number
                                                </small>

                                            </div>

                                        </div>


                                        {socialLinks.mobile ? (

                                            <a
                                                href={`tel:${socialLinks.mobile}`}
                                                className="text-decoration-none fw-semibold"
                                            >

                                                <FaPhoneAlt
                                                    size={13}
                                                    className="me-1"
                                                />

                                                {socialLinks.mobile}

                                            </a>

                                        ) : (

                                            <span className="text-muted">
                                                Not Added
                                            </span>

                                        )}

                                    </div>

                                </div>


                                {/* ==================================================
                                    STATUS
                                ================================================== */}

                                <div className="col-12 col-md-6">

                                    <div className="border rounded-4 p-3 h-100 bg-light">

                                        <div className="d-flex align-items-center mb-3">

                                            <div
                                                className="bg-warning bg-opacity-10 text-warning rounded-3 d-flex align-items-center justify-content-center me-3"
                                                style={{
                                                    width: "46px",
                                                    height: "46px"
                                                }}
                                            >

                                                <FaToggleOn size={25} />

                                            </div>


                                            <div>

                                                <h6 className="fw-bold mb-0">
                                                    Status
                                                </h6>

                                                <small className="text-muted">
                                                    Social Links Visibility
                                                </small>

                                            </div>

                                        </div>


                                        {socialLinks.status === "Enable" ? (

                                            <span className="badge rounded-pill bg-success-subtle text-success px-3 py-2">

                                                <FaCheckCircle
                                                    size={12}
                                                    className="me-1"
                                                />

                                                Enable

                                            </span>

                                        ) : (

                                            <span className="badge rounded-pill bg-secondary-subtle text-secondary px-3 py-2">

                                                Status Disabled

                                            </span>

                                        )}

                                    </div>

                                </div>


                            </div>


                            {/* ==================================================
                                ACTION BUTTONS
                            ================================================== */}

                            <div className="d-flex flex-wrap justify-content-end gap-2 mt-4 pt-4 border-top">

                                <button
                                    type="button"
                                    className="btn btn-outline-primary px-4"
                                    onClick={() =>
                                        navigate(
                                            `/edit-social-links/${socialLinks.id}`
                                        )
                                    }
                                >

                                    <FaEdit
                                        size={14}
                                        className="me-2"
                                    />

                                    Edit Social Links

                                </button>


                                <button
                                    type="button"
                                    className="btn btn-outline-danger px-4"
                                    onClick={handleDelete}
                                >

                                    <FaTrash
                                        size={13}
                                        className="me-2"
                                    />

                                    Delete

                                </button>

                            </div>


                        </>

                    )}

                </div>

            </div>

        </div>

    );
}


export default SocialLinks;